import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import TableForm from 'components/common/lists/TableForm';
import Footer from 'components/common/layout/Footer';
import SelectPatient from 'pages/modal/SelectPatient';
import styles from 'styles/components/common/lists/Table.module.scss';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron_right.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TTableColumn } from 'types/components/table';
import { getEvaluationChartListByRegisteredChart } from 'apis/chart/studentEvaluationChart';
import {
  TEvaluationChartListByRegisteredChartParams,
  TEvaluationChartByRegisteredChartTableInfo,
} from 'types/apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '60px',
  },
  {
    name: 'chartTypeName',
    label: '차트 종류',
    width: '220px',
  },
  {
    name: 'patientName',
    label: '대상자명',
    width: '120px',
  },
  {
    name: 'firstSubmitDate',
    label: '1차 제출 마감 기한',
    width: '250px',
  },
  {
    name: 'secondSubmitDate',
    label: ' 2차 제출 마감 기한',
    width: '250px',
  },
  { name: '', label: '', width: '120px' },
];

const EvaluationChart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const { openModal } = useModal();

  const [params, setParams] = useState({
    searchWord: query.get('searchWord') || undefined,
    courseInfoId: Number(query.get('courseInfoId')) || undefined,
    chartRound: Number(query.get('chartRound')) || undefined,
    isUnCreated: query.get('isUnCreated') === 'true' || false,
  });
  const [isLoading, setIsLoading] = useState(true);

  const { data: getEvaluationChartListByRegisteredChartData, isFetching } =
    useQuery(
      ['getEvaluationChartListByRegisteredChart', params],
      () =>
        getEvaluationChartListByRegisteredChart(
          params as TEvaluationChartListByRegisteredChartParams
        ),
      { enabled: !!params.courseInfoId && !!params.chartRound }
    );

  const tableData: TEvaluationChartByRegisteredChartTableInfo[] =
    getEvaluationChartListByRegisteredChartData?.data?.registeredChartList ||
    [];

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickRegister = ({
    registeredChartId,
    evaluationChartId,
    chartTypeNum,
    patientId,
    evaluationRound,
    isOpened,
    submitStartDate,
    isClosed,
    isEvaluationComplete,
  }: TEvaluationChartByRegisteredChartTableInfo) => {
    if (!isOpened && !isClosed) {
      openModal({
        title: '차트 작성 기간이 아닙니다.',
        text: `${submitStartDate}부터 작성할 수 있습니다.`,
      });
      return;
    }
    if (registeredChartId && chartTypeNum && !patientId && !isClosed) {
      // 제출 마감 기한 내 대상자 선택을 하지 않은 경우
      openModal({
        element: (
          <SelectPatient
            courseInfoId={Number(params.courseInfoId)}
            chartRound={Number(params.chartRound)}
            registeredChartId={registeredChartId}
            chartTypeNum={chartTypeNum}
          />
        ),
      });
    } else if (evaluationChartId && chartTypeNum && patientId) {
      const chartRoute = ChartInfo.find((chart) => chart.type === chartTypeNum)
        ?.route;
      if (!chartRoute) {
        openModal({
          title: '차트 조회 실패',
          text: '관리자에게 문의해 주세요.',
        });
        return;
      }
      sessionStorage.setItem('route', 'chart');
      // 제출 마감 기한 내: 차트 작성 페이지로 이동
      // 제출 마감 후: 차트 상세 페이지로 이동
      navigate(
        `/evaluation${chartRoute}/${evaluationChartId}${
          !isClosed ? '/register' : ''
        }?patientId=${patientId}&courseInfoId=${
          params.courseInfoId
        }&chartRound=${params.chartRound}&tab=${
          evaluationRound === 2
            ? isEvaluationComplete
              ? 3
              : 2
            : isEvaluationComplete
            ? 1
            : 0
        }`
      );
    } else {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
    }
  };

  const renderActions = (
    row: TEvaluationChartByRegisteredChartTableInfo,
    headerIndex?: number
  ) => (
    <>
      {headerIndex === 5 && (
        <button
          className={styles.link_s}
          disabled={row.isClosed && !row.evaluationChartId}
          onClick={() => onClickRegister(row)}
        >
          {/* 제출 마감 기한이 지났을 경우 '상세보기', 그 외 '작성하기' */}
          <span>{row.isClosed ? '상세보기' : '작성하기'}</span>
          <ChevronRightIcon
            fill={
              STYLES_VARIABLES[
                row.isClosed && !row.evaluationChartId ? 'GRAY_05' : 'PURPLE_01'
              ]
            }
            width={18}
            height={18}
          />
        </button>
      )}
    </>
  );

  useEffect(() => {
    setParams({
      ...params,
      searchWord: query.get('searchWord') || undefined,
      courseInfoId: Number(query.get('courseInfoId')) || undefined,
      chartRound: Number(query.get('chartRound')) || undefined,
      isUnCreated: query.get('isUnCreated') === 'true' || false,
    });
  }, [location.search]);

  return (
    <div className="min_h100vh flex flex_column">
      <div className="grow_1">
        <Layout bgColor={true} hideFooter={true}>
          <SearchFilter
            isCollective={true}
            input={{
              placeholder: '대상자 이름을 입력해 주세요.',
              width: '488px',
            }}
            dropdown={[
              {
                label: '강의명',
                id: 'courseClassInfo',
                placeholder: '강의명',
                width: '240px',
              },
              {
                label: '(회차) 회차명',
                id: 'chartRound',
                placeholder: '(회차) 회차명',
                width: '240px',
              },
            ]}
          />
        </Layout>

        <div className="table_container">
          <div className="flex justify_between items_center mt40 mb24">
            <h2 className="main_title">실습용 차트 목록</h2>
            <SearchFilter
              isInnerItem={true}
              checkbox={[
                {
                  id: 'isUnCreated',
                  label: '미등록한 실습차트만 보기',
                },
              ]}
            />
          </div>
          <TableForm<TEvaluationChartByRegisteredChartTableInfo>
            data={tableData}
            columns={tableColumns}
            renderActions={renderActions}
            emptyMessage="등록된 실습용 차트가 없습니다."
            isLoading={isLoading}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EvaluationChart;
